import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  ChatBubbleOutline as CommentIcon,
  SwapHoriz as StatusIcon,
  SubdirectoryArrowRight as SubStatusIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useApiQuery, useApiMutation } from "../../hooks/useApi";
import { getSocket } from "../../utils/socket";

function getActivityIcon(type) {
  switch (type) {
    case "COMMENT":
      return <CommentIcon sx={{ fontSize: 16, color: "#2563eb" }} />;
    case "STATUS_CHANGE":
      return <StatusIcon sx={{ fontSize: 16, color: "#16a34a" }} />;
    case "SUBSTATUS_CHANGE":
      return <SubStatusIcon sx={{ fontSize: 16, color: "#d97706" }} />;
    default:
      return <StatusIcon sx={{ fontSize: 16, color: "#64748b" }} />;
  }
}

function formatTime(createdAt) {
  if (!createdAt) return "";
  return new Date(createdAt).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function capitalizeName(name) {
  if (!name) return name;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Renders one row for Comments section
function CommentItem({ item }) {
  const actorName = capitalizeName(item.actorId?.name || "User");
  const isAdmin = item.actorId?.role === "admin" || item.actorRole === "admin";
  const timeStr = formatTime(item.createdAt);

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          backgroundColor: "#ffffff",
          border: "1px solid #cbd5e1",
          mt: 0.25,
        }}
      >
        {getActivityIcon("COMMENT")}
      </Avatar>

      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
              {actorName}
            </Typography>
            {isAdmin && (
              <Chip
                label="ADMIN"
                color="error"
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  borderRadius: "4px",
                  px: 0.5,
                }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {timeStr}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "#334155", mt: 0.25 }}>
          {item.message}
        </Typography>
      </Box>
    </Box>
  );
}

// Renders one row for Job Activity section (status/sub-status changes)
function ActivityItem({ item }) {
  const actorName = capitalizeName(item.actorId?.name || "User");
  const timeStr = formatTime(item.createdAt);
  const reason = item.metadata?.reason;

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          backgroundColor: "#ffffff",
          border: "1px solid #cbd5e1",
          mt: 0.25,
        }}
      >
        {getActivityIcon(item.type)}
      </Avatar>

      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {actorName}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {timeStr}
          </Typography>
        </Box>

        {item.type === "STATUS_CHANGE" && (
          <Box sx={{ mt: 0.5, }}>
            <Typography variant="body2" sx={{ color: "#166534", fontWeight: 600 }}>
              Status changed
            </Typography>
            <Typography variant="body2" sx={{ color: "#", border: ".05px solid", px: "5px", py: "5px", borderRadius: "10px", width: "fit-content" }}>
              {item.metadata?.previousStatus || "new"} → <b>{item.metadata?.newStatus}</b>
            </Typography>
            {reason && (
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                Reason: {reason}
              </Typography>
            )}
          </Box>
        )}

        {item.type === "SUBSTATUS_CHANGE" && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 600 }}>
              Sub-status changed
            </Typography>
            <Typography variant="body2" sx={{ color: "#", border: ".05px solid", px: "5px", py: "5px", borderRadius: "10px", width: "fit-content" }}>
              {item.metadata?.previousSubStatus || "none"} → <b>{item.metadata?.newSubStatus}</b>
            </Typography>
            {reason && (
              <Typography variant="caption" sx={{ fontSize: "15px", color: "#64748b", display: "block", mt: 0.25 }}>
                Reason: {reason}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function JobActivityTimeline({ jobId, jobCustomId, onCommentAdded }) {
  const [commentText, setCommentText] = useState("");
  const [timelineItems, setTimelineItems] = useState([]);

  const { data, isLoading, error, refetch } = useApiQuery(
    ["job-activity-timeline", jobId],
    `/jobs/${jobId}/activity`,
    { enabled: Boolean(jobId) }
  );

  const addCommentMutation = useApiMutation(
    `/jobs/${jobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        setCommentText("");
        refetch();
        if (onCommentAdded) onCommentAdded();
      },
    }
  );

  // Sync API response with local state
  useEffect(() => {
    if (data?.activities) {
      setTimelineItems(data.activities);
    }
  }, [data]);

  // Real-time room subscription for job
  useEffect(() => {
    if (!jobId) return;

    const socket = getSocket();

    const joinRoom = () => {
      socket.emit("join-job", jobId);
    };

    if (socket.connected) {
      joinRoom();
    }
    socket.on("connect", joinRoom);

    const handleNewActivity = (newAct) => {
      if (newAct && (newAct.jobId === jobId || newAct.jobCustomId === jobCustomId)) {
        setTimelineItems((prev) => {
          if (prev.some((item) => item._id === newAct._id)) return prev;
          return [...prev, newAct];
        });
      }
    };

    socket.on("new-activity", handleNewActivity);

    return () => {
      socket.emit("leave-job", jobId);
      socket.off("connect", joinRoom);
      socket.off("new-activity", handleNewActivity);
    };
  }, [jobId, jobCustomId]);

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    addCommentMutation.mutate({ commentText: commentText.trim() });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Unable to load activity timeline: {error.message}
      </Alert>
    );
  }

  const comments = timelineItems.filter((item) => item.type === "COMMENT");
  const activityItems = timelineItems.filter(
    (item) => item.type === "STATUS_CHANGE" || item.type === "SUBSTATUS_CHANGE"
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── COMMENTS SECTION ── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#1e293b" }}>
        Comments
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: "10px",
          borderColor: "#e2e8f0",
          backgroundColor: "#",
          maxHeight: 260,
          overflowY: "auto",
        }}
      >
        {comments.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", textAlign: "center", py: 2 }}>
            No comments yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {comments.map((item, index) => (
              <Box key={item._id || index}>
                <CommentItem item={item} />
                {index < comments.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Comment Form */}
      <Box component="form" onSubmit={handleSubmitComment} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Write a comment..."
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            mb: 1.5,
            backgroundColor: "#ffffff",
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!commentText.trim() || addCommentMutation.isPending}
            startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#2563eb",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          >
            {addCommentMutation.isPending ? "Posting..." : "Comment"}
          </Button>
        </Box>
      </Box>

      {/* ── JOB ACTIVITY SECTION ── */}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#1e293b" }}>
        Job Activity
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "10px",
          borderColor: "#e2e8f0",
          backgroundColor: "#",
          maxHeight: 260,
          overflowY: "auto",
        }}
      >
        {activityItems.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", textAlign: "center", py: 2 }}>
            No status changes recorded for this job yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {activityItems.map((item, index) => (
              <Box key={item._id || index}>
                <ActivityItem item={item} />
                {index < activityItems.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
